import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, Grid, Chip, Divider, Avatar, Tooltip, List,
  ListItem, ListItemAvatar, ListItemText, Button, IconButton
} from '@mui/material';
import { Assignment, Group, CalendarToday, Phone, AccessTime } from '@mui/icons-material';
import { projectsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DevBadge from '../../components/common/DevBadge';
import dayjs from 'dayjs';

const STATUS_COLORS = { active: 'success', paused: 'warning', completed: 'default' };
const STATUS_LABELS = { active: 'Activo', paused: 'En pausa', completed: 'Terminado' };

export default function MemberProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    projectsAPI.getAll()
      .then(res => setProjects(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <DevBadge id="page:member-proyectos" />
      <Typography variant="h5" fontWeight={700} mb={0.5}>Mis Proyectos</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Proyectos en los que participas actualmente
      </Typography>

      {projects.length === 0 ? (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <Assignment sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No estás asignado a ningún proyecto aún.</Typography>
          <Typography variant="body2" color="text.secondary">Tu líder de equipo te asignará proyectos pronto.</Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {projects.map(proj => (
            <Grid item xs={12} sm={6} md={4} key={proj._id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
                <Box sx={{ bgcolor: '#1B2B4B', px: 2.5, py: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff', flexGrow: 1, mr: 1 }}>
                      {proj.name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.75} sx={{ flexShrink: 0 }}>
                      <Chip
                        label={STATUS_LABELS[proj.status] || proj.status}
                        size="small"
                        color={STATUS_COLORS[proj.status]}
                      />
                      <Tooltip title="Ver actividades">
                        <IconButton size="small"
                          onClick={() => navigate(`/member/activities?projectId=${proj._id}`)}
                          sx={{ color: 'rgba(255,255,255,0.75)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                          <AccessTime sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  {proj.clientId?.name && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {proj.clientId.name}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ p: 2.5, flex: 1 }}>
                  {proj.description && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {proj.description}
                    </Typography>
                  )}
                  <Divider sx={{ mb: 2 }} />
                  {proj.createdAt && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(proj.createdAt).format('MMM YYYY')}
                      </Typography>
                    </Box>
                  )}
                  {proj.members?.length > 0 && (
                    <Box mt={2}>
                      <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                        <Group sx={{ fontSize: 15, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Equipo ({proj.members.length})
                        </Typography>
                      </Box>
                      <List dense disablePadding>
                        {proj.members.map(m => {
                          const name = m.name || m.userId?.name;
                          const lastName = m.userId?.lastName;
                          const email = m.email || m.userId?.email;
                          const phone = m.userId?.phone;
                          const initials = ((name || email || '?')[0]).toUpperCase();
                          const displayName = [name, lastName].filter(Boolean).join(' ') || email || 'Sin nombre';
                          return (
                            <ListItem key={(m.userId?._id || m.userId)?.toString() || email}
                              disableGutters sx={{ py: 0.3 }}>
                              <ListItemAvatar sx={{ minWidth: 36 }}>
                                <Avatar sx={{ width: 28, height: 28, fontSize: 12,
                                  bgcolor: 'rgb(132, 0, 255)', color: '#fff', fontWeight: 700 }}>
                                  {initials}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" fontWeight={500} noWrap>
                                    {displayName}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {email}
                                  </Typography>
                                }
                              />
                              {phone && (
                                <Tooltip title={
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <Phone sx={{ fontSize: 13 }} />
                                    <span>{phone}</span>
                                  </Box>
                                } arrow>
                                  <Phone sx={{ fontSize: 15, color: 'text.disabled',
                                    cursor: 'default', flexShrink: 0, ml: 0.5 }} />
                                </Tooltip>
                              )}
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
